import { useEffect, useState } from 'react';
import { LiveProduct } from './utils';

export interface CatalogVariant {
  id: string;
  title: string;
  price: string | null;
  compareAt: string | null;
  available: boolean;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  featuredImage: string | null;
}

export interface CatalogOption {
  name: string;
  position: number;
  values: string[];
}

export interface CatalogProduct {
  id: string;
  title: string;
  handle: string | null;
  price: string | null;
  compareAt: string | null;
  image: string | null;
  images: string[];
  variantId: string | null;
  bodyHtml: string | null;
  options: CatalogOption[];
  variants: CatalogVariant[];
}

const cache = new Map<string, CatalogProduct>();
let loadPromise: Promise<void> | null = null;

function mapProduct(p: any): CatalogProduct {
  const variants: CatalogVariant[] = (p.variants || []).map((v: any) => ({
    id: String(v.id),
    title: v.title || '',
    price: v.price != null ? String(v.price) : null,
    compareAt: v.compare_at_price != null ? String(v.compare_at_price) : null,
    available: v.available !== false,
    option1: v.option1 != null ? String(v.option1) : null,
    option2: v.option2 != null ? String(v.option2) : null,
    option3: v.option3 != null ? String(v.option3) : null,
    featuredImage: v.featured_image?.src || null,
  }));
  const first = variants[0];
  const images = (p.images || [])
    .map((img: any) => img?.src)
    .filter(Boolean) as string[];

  return {
    id: String(p.id),
    title: p.title,
    handle: p.handle || null,
    price: first?.price ?? null,
    compareAt: first?.compareAt ?? null,
    image: images[0] || first?.featuredImage || null,
    images,
    variantId: first?.id ?? null,
    bodyHtml: p.body_html || null,
    options: (p.options || []).map((o: any, i: number) => ({
      name: o.name || `Option ${i + 1}`,
      position: o.position || i + 1,
      values: Array.isArray(o.values) ? o.values.map(String) : [],
    })),
    variants,
  };
}

const CATALOG_PAGE_SIZE = 250;

/** Load every published product from the Ajax API (Shopify caps each page at 250). */
export async function ensureCatalog(): Promise<void> {
  if (cache.size > 0) return;
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        let page = 1;
        while (true) {
          const res = await fetch(`/products.json?limit=${CATALOG_PAGE_SIZE}&page=${page}`);
          if (!res.ok) break;
          const data = await res.json();
          const products = data.products || [];
          if (!products.length) break;
          for (const p of products) {
            cache.set(String(p.id), mapProduct(p));
          }
          if (products.length < CATALOG_PAGE_SIZE) break;
          page += 1;
        }
      } catch {
        // ignore
      }
    })();
  }
  await loadPromise;
}

export function getCatalogProduct(productId: string): CatalogProduct | undefined {
  return cache.get(String(productId));
}

function fallbackInfo(product: LiveProduct): CatalogProduct {
  return {
    id: String(product.item_id),
    title: product.item_name || product.item_id,
    handle: null,
    price: null,
    compareAt: null,
    image: null,
    images: [],
    variantId: null,
    bodyHtml: null,
    options: [],
    variants: [],
  };
}

export function useCatalogProduct(product: LiveProduct | null | undefined): CatalogProduct | null {
  const [info, setInfo] = useState<CatalogProduct | null>(() => {
    if (!product) return null;
    return getCatalogProduct(String(product.item_id)) || fallbackInfo(product);
  });

  useEffect(() => {
    if (!product) {
      setInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      await ensureCatalog();
      if (cancelled) return;
      setInfo(getCatalogProduct(String(product.item_id)) || fallbackInfo(product));
    })();
    return () => {
      cancelled = true;
    };
  }, [product?.item_id, product?.item_name]);

  return info;
}

export function useCatalogMap(products: LiveProduct[] | undefined): Map<string, CatalogProduct> {
  const [map, setMap] = useState(() => new Map(cache));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await ensureCatalog();
      if (!cancelled) setMap(new Map(cache));
    })();
    return () => {
      cancelled = true;
    };
  }, [products?.length]);

  return map;
}

/** First letter of a title, skipping a leading tag like `[MALAYSIA DAY]`. */
export function thumbLetter(title?: string | null): string {
  const text = String(title || '').trim();
  const match = text.match(/[A-Za-z0-9]/);
  return match ? match[0] : '?';
}

export function formatMoney(price: string | number | null | undefined): string {
  if (price == null || price === '') return '';
  const n = typeof price === 'number' ? price : Number(price);
  if (Number.isNaN(n)) return String(price);
  return n.toFixed(2);
}

export function findVariant(
  product: CatalogProduct,
  selections: (string | null)[]
): CatalogVariant | null {
  const [o1, o2, o3] = selections;
  return (
    product.variants.find(
      (v) =>
        (o1 == null || v.option1 === o1) &&
        (o2 == null || v.option2 === o2) &&
        (o3 == null || v.option3 === o3)
    ) || null
  );
}

/** Whether any available variant matches current selections with optionIndex forced to value */
export function isOptionValueAvailable(
  product: CatalogProduct,
  optionIndex: number,
  value: string,
  selections: (string | null)[]
): boolean {
  const next = [...selections];
  next[optionIndex] = value;
  return product.variants.some((v) => {
    if (!v.available) return false;
    if (next[0] != null && v.option1 !== next[0]) return false;
    if (next[1] != null && v.option2 !== next[1]) return false;
    if (next[2] != null && v.option3 !== next[2]) return false;
    return true;
  });
}

export function getVariantById(
  product: CatalogProduct,
  variantId: string | null | undefined
): CatalogVariant | null {
  if (!variantId) return product.variants[0] || null;
  return product.variants.find((v) => v.id === String(variantId)) || product.variants[0] || null;
}

/** True when shopper must pick options (not Default Title / single SKU). */
export function needsVariantPicker(product: CatalogProduct | null | undefined): boolean {
  if (!product) return false;
  if (product.variants.length > 1) return true;
  return product.options.some(
    (o) => o.values.length > 1 || (o.values[0] != null && o.values[0] !== 'Default Title')
  );
}

export function meaningfulOptions(product: CatalogProduct) {
  return product.options.filter(
    (o) => o.values.length > 1 || (o.values[0] != null && o.values[0] !== 'Default Title')
  );
}
